/**
 * Full-screen photo grid for managing a listing's photos.
 *
 * Drag any tile to reorder; whichever tile lands in position 0 becomes
 * the cover photo automatically (we fire the existing set-cover endpoint
 * right after the reorder POST succeeds, so the rest of the app — public
 * listing pages, lightbox, hero — keep using the same `is_cover` flag).
 *
 * Upload, delete, and lightbox preview live inside this dialog so the
 * Edit Listing form stays uncluttered (a single "Manage photos" button
 * over there replaces the old inline drag list).
 */
import { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Loader2, ImagePlus, Image as ImageIcon } from 'lucide-react';

export function PhotoManagerDialog({
  open, onOpenChange,
  photos = [],
  uploading = false,
  onUpload,          // (event) => Promise
  onDelete,          // (photoId) => Promise
  onReorder,         // (newOrderedPhotos) => Promise — server reorder + cover sync handled by caller
  onPreview,         // (idx) => void (opens lightbox)
  apiBase = '',
}) {
  const fileInputRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleDragStart = (idx) => (e) => {
    setDragIdx(idx);
    // Empty drag image so the browser doesn't paint a ghost — looks
    // cleaner because we're already styling the source tile as faded.
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  };
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const handleDrop = async (dropIdx) => {
    if (dragIdx == null || dragIdx === dropIdx) {
      setDragIdx(null); setDragOverIdx(null); return;
    }
    const newOrder = [...photos];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(dropIdx, 0, moved);
    setDragIdx(null); setDragOverIdx(null);
    if (onReorder) await onReorder(newOrder);
  };

  const handleDelete = async (photoId) => {
    setDeletingId(photoId);
    try { await onDelete?.(photoId); }
    finally { setDeletingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[min(1400px,95vw)] max-h-[92vh] overflow-hidden flex flex-col p-0"
        style={{ zIndex: 80 }}
        data-testid="photo-manager-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-base font-semibold">
                All photos · <span className="text-muted-foreground font-normal">{photos.length}</span>
              </DialogTitle>
              <DialogDescription className="text-xs">
                Drag any photo to reorder. The first photo (top-left) is always the cover.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={async (e) => { await onUpload?.(e); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="hidden"
                data-testid="pm-file-input"
              />
              <Button
                size="sm" variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="h-8 text-xs gap-1"
                data-testid="pm-upload-btn"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Add photos
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {photos.length === 0 ? (
            <div className="h-72 flex flex-col items-center justify-center text-muted-foreground gap-2">
              <ImagePlus className="h-10 w-10 opacity-40" />
              <p className="text-sm">No photos yet</p>
              <Button
                size="sm" variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs"
              >
                <Plus className="h-3 w-3 mr-1" /> Upload your first photos
              </Button>
            </div>
          ) : (
            <div
              className="grid gap-3 sm:gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
              data-testid="pm-grid"
            >
              {photos.map((photo, idx) => {
                const isFirst = idx === 0;
                const isDraggingThis = dragIdx === idx;
                const isOver = dragOverIdx === idx && dragIdx !== idx;
                return (
                  <div
                    key={photo.id}
                    draggable
                    onDragStart={handleDragStart(idx)}
                    onDragOver={handleDragOver(idx)}
                    onDrop={() => handleDrop(idx)}
                    onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                    className={`group relative rounded-2xl overflow-hidden bg-muted cursor-grab active:cursor-grabbing transition-all
                      ${isDraggingThis ? 'opacity-40 scale-95' : ''}
                      ${isOver ? 'ring-2 ring-primary ring-offset-2 scale-[1.02]' : ''}
                    `}
                    style={{ aspectRatio: '3 / 4' }}
                    data-testid={`pm-tile-${photo.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => onPreview?.(idx)}
                      className="absolute inset-0 w-full h-full"
                      aria-label={`Preview photo ${idx + 1}`}
                    >
                      <img
                        src={photo.url?.startsWith('http') ? photo.url : `${apiBase}${photo.url}`}
                        alt={photo.filename || `Photo ${idx + 1}`}
                        className="w-full h-full object-cover pointer-events-none"
                        loading="lazy"
                        draggable={false}
                      />
                    </button>

                    {/* Cover badge — always on the first tile, persistent. */}
                    {isFirst && (
                      <span
                        className="absolute top-2 left-2 bg-white/95 text-[#1f2933] text-[11px] font-medium px-2.5 py-1 rounded-full shadow-sm pointer-events-none"
                        data-testid={`pm-cover-${photo.id}`}
                      >
                        Cover photo
                      </span>
                    )}

                    {/* Hover delete — bottom-right so it doesn't overlap the cover badge. */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                      disabled={deletingId === photo.id}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 hover:bg-red-50 text-destructive rounded-full p-1.5 shadow-sm disabled:opacity-50"
                      title="Delete photo"
                      data-testid={`pm-delete-${photo.id}`}
                    >
                      {deletingId === photo.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Trash2 className="h-3.5 w-3.5" />}
                    </button>

                    {/* Position pip — small index in the bottom-left while hovering or dragging,
                        helps the user track where the new top tile is going to land. */}
                    <span className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/55 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full pointer-events-none">
                      {idx + 1}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t flex items-center justify-between bg-muted/20">
          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
            <ImageIcon className="h-3 w-3" />
            Drag any tile to reorder — the top-left tile is automatically the cover.
          </p>
          <Button size="sm" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="pm-close-btn">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
